/**
 * ui/DisplaysLocal — onglet « 📺 Écrans » en mode LAN (admin.exe).
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * Pendant local de `ticketflow/DisplaysApproval` (console centrale). Même besoin —
 * voir et régler les écrans d'un site — mais par la route LOCALE `/api/displays`
 * (JWT admin/manager), donc SANS la console. C'est le cas qui compte : quand le
 * central est injoignable, admin.exe est le seul outil sur place, et l'onglet
 * n'existait pas du tout.
 *
 * Ce qu'on peut faire ici : nommer, rendre muet, tester le son, oublier un écran.
 * Ce qu'on ne peut PAS faire : approuver (`enabled`). Le serveur refuse ce champ sur
 * la route locale — l'approbation d'un écran facturé n'est valable que signée par
 * l'éditeur. On affiche donc l'état d'approbation ET l'état du quota de licence,
 * pour que « écran noir » cesse d'être une énigme sur place : soit la licence
 * l'inclut, soit il faut un ordre du central.
 */
import React, { useCallback, useEffect, useState } from 'react';

/** Phrase d'explication du quota — c'est elle qui évite l'appel téléphonique. */
function texteQuota(q) {
  if (!q) return '';
  if (q.quota === null) {
    return "La licence de ce serveur ne précise aucun nombre d'écrans : tout nouvel écran reste en attente d'une approbation de la console centrale.";
  }
  const restants = q.restants ?? 0;
  const base = `Licence : ${q.quota} écran(s) inclus — ${q.utilises} utilisé(s)`;
  if (q.depasse) return `${base}. Quota DÉPASSÉ : les écrans en trop restent en attente.`;
  return `${base}, ${restants} place(s) restante(s).`;
}

export default function DisplaysLocal({ api, role }) {
  const [list, setList] = useState(null);
  const [quota, setQuota] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [testing, setTesting] = useState('');
  const estAdmin = role === 'admin';

  const load = useCallback(async () => {
    setErr('');
    try {
      setList(await api.listDisplays());
    } catch (e) {
      setErr(e?.response?.data?.error || String(e?.message || e));
      setList([]);
    }
    // Le quota vient d'une route ajoutée en 0.2.0 : un serveur plus ancien répond 404.
    // Ce n'est pas une erreur à afficher — la liste, elle, reste parfaitement utile.
    try { setQuota(await api.getDisplaysQuota()); } catch { setQuota(null); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const renommer = async (displayId, name) => {
    setBusy(displayId); setErr('');
    try { await api.updateDisplay(displayId, { name }); await load(); }
    catch (e) { setErr(e?.response?.data?.error || String(e?.message || e)); }
    finally { setBusy(''); }
  };

  const basculerSon = async (displayId, sound) => {
    setBusy(displayId); setErr('');
    try { await api.updateDisplay(displayId, { sound }); await load(); }
    catch (e) { setErr(e?.response?.data?.error || String(e?.message || e)); }
    finally { setBusy(''); }
  };

  const tester = async (displayId, kind) => {
    setTesting(`${displayId}:${kind}`); setErr('');
    try { await api.testDisplay(displayId, kind); }
    catch (e) { setErr(e?.response?.data?.error || String(e?.message || e)); }
    finally { setTesting(''); }
  };

  /**
   * Oublier un écran (admin). Geste utile sur site — un écran remplacé ou déplacé encombre
   * la liste — mais qui n'est PAS neutre vis-à-vis de l'approbation : l'écran oublié se
   * ré-enregistre à sa prochaine connexion, et son approbation est alors RECALCULÉE d'après
   * la licence (`store.ts:140`). Un écran approuvé au-delà du quota revient donc en attente,
   * et un écran suspendu par l'éditeur peut revenir actif s'il reste une place. On le dit
   * franchement dans la confirmation plutôt que de le laisser découvrir après coup.
   */
  const oublier = async (displayId) => {
    if (!window.confirm(
      `Oublier l'écran ${displayId} ?\n\n`
      + "Il réapparaîtra dès qu'il se reconnectera, et son approbation sera recalculée "
      + "d'après la licence — une approbation accordée par la console peut donc être perdue.",
    )) return;
    setBusy(displayId); setErr('');
    try { await api.forgetDisplay(displayId); await load(); }
    catch (e) { setErr(e?.response?.data?.error || String(e?.message || e)); }
    finally { setBusy(''); }
  };

  const sonGlobal = async (sound) => {
    setBusy('*'); setErr('');
    try { await api.setAllDisplaysSound(sound); await load(); }
    catch (e) { setErr(e?.response?.data?.error || String(e?.message || e)); }
    finally { setBusy(''); }
  };

  return (
    <div className="card">
      <h2>
        Écrans <button type="button" className="ghost" onClick={load}>↻</button>
      </h2>
      <p className="muted">
        Réglages locaux de ce serveur : nom, son, test audio. L’<b>approbation</b> d’un écran
        (facturation) reste une prérogative de la console centrale — elle n’est valable que signée.
      </p>
      {quota && <div className="status">{texteQuota(quota)}</div>}
      {err && <div className="status">{err}</div>}

      {list === null ? <p className="muted">…</p> : list.length === 0 ? (
        <p className="muted">Aucun écran connu de ce serveur. Un écran apparaît ici dès sa première connexion.</p>
      ) : (
        <>
          <div className="nav-tools" style={{ marginBottom: 10 }}>
            <button type="button" className="ghost" disabled={busy === '*'} onClick={() => sonGlobal(true)}>🔊 Son sur tous</button>{' '}
            <button type="button" className="ghost" disabled={busy === '*'} onClick={() => sonGlobal(false)}>🔇 Couper tous</button>
          </div>
          <table className="perm-matrix">
            <thead>
              <tr><th>Écran</th><th>État</th><th>Son</th><th>Test audio</th><th>Approbation</th>{estAdmin && <th />}</tr>
            </thead>
            <tbody>
              {list.map((d) => (
                <tr key={d.displayId}>
                  <td>
                    {/* Renommage à la volée : enregistré à la sortie du champ, pas de bouton
                        supplémentaire pour un geste aussi courant qu'étiqueter « Accueil ». */}
                    <input
                      defaultValue={d.name}
                      disabled={busy === d.displayId}
                      onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== d.name) renommer(d.displayId, v); }}
                    />
                    <div className="muted">{d.displayId}</div>
                  </td>
                  <td>{d.online ? <span className="badge">en ligne</span> : <span className="muted">hors ligne</span>}</td>
                  <td>
                    <span className={d.sound ? 'badge' : 'muted'}>{d.sound ? '🔊 master' : '🔇 slave'}</span>{' '}
                    <button type="button" className="ghost" disabled={busy === d.displayId} onClick={() => basculerSon(d.displayId, !d.sound)}>
                      {d.sound ? 'Rendre muet' : 'Activer'}
                    </button>
                  </td>
                  <td>
                    <button type="button" className="ghost" disabled={!d.online || testing === `${d.displayId}:beep`} onClick={() => tester(d.displayId, 'beep')}>
                      {testing === `${d.displayId}:beep` ? '…' : '🔔 Bip'}
                    </button>{' '}
                    <button type="button" className="ghost" disabled={!d.online || testing === `${d.displayId}:voice`} onClick={() => tester(d.displayId, 'voice')}>
                      {testing === `${d.displayId}:voice` ? '…' : '🗣 Voix'}
                    </button>
                  </td>
                  <td>
                    {d.enabled
                      ? <span className="badge">✅ approuvé</span>
                      : <span className="muted" title="Approbation par la console centrale, ou automatique si la licence inclut encore une place.">⛔ en attente</span>}
                  </td>
                  {estAdmin && (
                    <td>
                      <button type="button" className="ghost danger" disabled={busy === d.displayId} onClick={() => oublier(d.displayId)}>Oublier</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
